/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import apiClient from '../api/client';

/**
 * Builds an error shaped like an axios error (`err.response.{status,data}`) so
 * streamed failures go through the same helpers (getApiErrorMessage, ...).
 */
const httpError = (status, data) => {
  const error = new Error(data?.message || `Request failed with status ${status}`);
  error.response = { status, data };
  return error;
};

/** Splits an SSE text buffer into complete events; returns [events, remainder]. */
const takeSseEvents = (buffer) => {
  const events = [];
  const blocks = buffer.split(/\r?\n\r?\n/);
  const remainder = blocks.pop();
  blocks.forEach((block) => {
    let event = 'message';
    const data = [];
    block.split(/\r?\n/).forEach((line) => {
      if (line.startsWith('event:')) { event = line.slice(6).trim(); }
      else if (line.startsWith('data:')) { data.push(line.slice(5).replace(/^ /, '')); }
    });
    if (data.length) {
      try {
        events.push({ event, payload: JSON.parse(data.join('\n')) });
      } catch {
        // Ignore malformed events; the server only sends JSON payloads.
      }
    }
  });
  return [events, remainder];
};

export const aiAgentService = {
  // GET /api/ai-agents?accountId=
  getAiAgents: (params = {}) => apiClient.get('/ai-agents', { params }),

  // POST /api/ai-agents (body: { accountId?, name, role, type, provider, model, ... })
  createAiAgent: (payload) => apiClient.post('/ai-agents', payload),

  // GET /api/ai-agents/:id?accountId=
  getAiAgent: (id, params = {}) => apiClient.get(`/ai-agents/${encodeURIComponent(id)}`, { params }),

  // PATCH /api/ai-agents/:id?accountId= (server sends POST /v1/agents/:id upstream)
  updateAiAgent: (id, payload, params = {}) =>
    apiClient.patch(`/ai-agents/${encodeURIComponent(id)}`, payload, { params }),

  // POST /api/ai-agents/:id/invoke?accountId= (non-streaming; the chat preview's fallback when streaming gets a 401; body: { query, sessionId? })
  invokeAiAgent: (id, payload, params = {}) =>
    apiClient.post(`/ai-agents/${encodeURIComponent(id)}/invoke`, { ...payload, stream: false }, { params }),

  /**
   * Streaming chat preview: POST /api/ai-agents/:id/invoke with `stream: true`.
   * Uses fetch (axios cannot read a response stream in the browser), sending the
   * same headers as apiClient. Calls `onToken(text)` for each piece of the reply
   * and `onAction(action)` for each device action
   * (`{ id, action, params, requiresConfirmation, spoken }`), and resolves with
   * the final `{ sessionId, messageId, answer, citations, actions }`.
   * Rejects with an axios-shaped error (`err.response.status/data`) on failure;
   * aborting `signal` rejects with an AbortError.
   */
  streamAiAgent: async (id, payload, params = {}, { onToken, onAction, signal } = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    const url = `${apiClient.defaults.baseURL}/ai-agents/${encodeURIComponent(id)}/invoke${query ? `?${query}` : ''}`;

    const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
    Object.entries(apiClient.defaults.headers.common || {}).forEach(([key, value]) => {
      if (typeof value === 'string') { headers[key] = value; }
    });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...payload, stream: true }),
      signal,
    });

    // Failures before streaming starts come back as normal JSON errors.
    if (!response.ok || !(response.headers.get('content-type') || '').includes('text/event-stream')) {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) { throw httpError(response.status, data); }
      // Server answered with JSON after all — treat it as a complete reply.
      return data?.data || {};
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let answer = '';
    const actions = [];
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) { break; }
      buffer += decoder.decode(value, { stream: true });
      const [events, remainder] = takeSseEvents(buffer);
      buffer = remainder;
      for (const { event, payload: data } of events) {
        if (event === 'token' && data?.text) {
          answer += data.text;
          if (onToken) { onToken(data.text); }
        } else if (event === 'action' && data?.action) {
          actions.push(data.action);
          if (onAction) { onAction(data.action); }
        } else if (event === 'error') {
          throw httpError(502, { message: data?.message || 'The agent could not answer. Please try again.' });
        } else if (event === 'done') {
          return { ...data, answer: data?.answer || answer, actions: data?.actions?.length ? data.actions : actions };
        }
      }
    }
    // Stream closed without a final event — return what arrived.
    return { sessionId: null, messageId: null, answer, citations: [], actions };
  },

  // DELETE /api/ai-agents/:id?accountId=
  deleteAiAgent: (id, params = {}) => apiClient.delete(`/ai-agents/${encodeURIComponent(id)}`, { params }),
};
